import React from 'react';
import { Helmet } from 'react-helmet';
import Header from 'components/ui/Header';
import Footer from 'components/ui/Footer';
import Icon from 'components/AppIcon';
import { Link } from 'react-router-dom';

const AISolutionsIntegrationHubPage = () => {
  const aiModules = [
    {
      icon: 'Lightbulb',
      title: 'Introduction à l\'IA pour décideurs',
      description: 'Comprendre les fondamentaux de l\'IA et son potentiel stratégique pour votre organisation.',
      features: [
        'Concepts fondamentaux de l\'IA et Machine Learning',
        'Cas d\'usage et opportunités business',
        'ROI et stratégie d\'intégration IA',
        'Tendances et futur de l\'IA'
      ]
    },
    {
      icon: 'Users',
      title: 'L\'IA au service du management',
      description: 'Exploitez l\'intelligence artificielle pour optimiser vos processus de management et de décision.',
      features: [
        'Prise de décision augmentée par l\'IA',
        'Automatisation des tâches managériales',
        'Analytics prédictifs pour le pilotage',
        'Gestion d\'équipe assistée par IA'
      ]
    },
    {
      icon: 'Zap',
      title: 'Outils d\'IA générative pour la productivité',
      description: 'Maîtrisez ChatGPT, Midjourney et autres outils d\'IA générative pour décupler votre efficacité.',
      features: [
        'ChatGPT et prompts engineering',
        'Génération de contenu et création visuelle',
        'Automatisation de workflows avec IA',
        'Intégration dans outils métier'
      ]
    },
    {
      icon: 'Heart',
      title: 'IA appliquée aux RH',
      description: 'Transformez votre fonction RH avec l\'intelligence artificielle pour recruter, former et fidéliser.',
      features: [
        'Recrutement intelligent et matching candidats',
        'Chatbots RH et onboarding automatisé',
        'Analyse prédictive du turnover',
        'Personnalisation des parcours de formation'
      ]
    },
    {
      icon: 'TrendingUp',
      title: 'IA appliquée au marketing',
      description: 'Boostez vos performances marketing avec l\'IA : ciblage, personnalisation et optimisation.',
      features: [
        'Segmentation client et ciblage prédictif',
        'Génération de contenu marketing',
        'Chatbots conversationnels et support client',
        'Optimisation campagnes et ROI'
      ]
    },
    {
      icon: 'Shield',
      title: 'Éthique et gouvernance de l\'IA',
      description: 'Intégrez l\'IA de manière responsable avec une gouvernance solide et éthique.',
      features: [
        'Principes éthiques et biais algorithmiques',
        'RGPD et protection des données',
        'Gouvernance et comité IA',
        'Transparence et explicabilité'
      ]
    },
    {
      icon: 'Award',
      title: 'Chef de projet Intelligence Artificielle (certification)',
      description: 'Formation certifiante pour piloter avec succès vos projets d\'IA de bout en bout.',
      features: [
        'Cadrage et faisabilité projets IA',
        'Gestion des données et modèles ML',
        'Déploiement et MLOps',
        'Certification reconnue'
      ]
    }
  ];

  const whyChooseUs = [
    {
      icon: 'Brain',
      title: 'Expertise IA Avancée',
      description: 'Formateurs experts en IA avec expérience terrain dans l\'intégration de solutions IA.'
    },
    {
      icon: 'Target',
      title: 'Approche Pratique',
      description: 'Formations orientées cas d\'usage réels et immédiatement applicables dans votre organisation.'
    },
    {
      icon: 'Zap',
      title: 'Technologies Actuelles',
      description: 'Maîtrise des derniers outils d\'IA générative : ChatGPT, Midjourney, Claude, Gemini.'
    },
    {
      icon: 'Shield',
      title: 'Éthique & Conformité',
      description: 'Intégration responsable de l\'IA avec respect du RGPD et des principes éthiques.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Intelligence Artificielle | CRF PERFECTION - Formations IA 2026</title>
        <meta 
          name="description" 
          content="Formations Intelligence Artificielle 2026 : Introduction à l'IA pour décideurs, IA au service du management, outils d'IA générative, IA appliquée aux RH/marketing, éthique et gouvernance de l'IA, chef de projet IA certification."
        />
        <meta name="keywords" content="formation IA, intelligence artificielle, ChatGPT, IA générative, chef de projet IA, éthique IA, gouvernance IA, IA RH, IA marketing" />
        <meta property="og:title" content="Intelligence Artificielle | CRF PERFECTION" />
        <meta 
          property="og:description" 
          content="Maîtrisez l'Intelligence Artificielle avec nos formations 2026 : IA pour décideurs, management, outils génératifs, applications RH/marketing et certification."
        />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <Header />
        
        {/* Section 1: Hero avec Introduction */}
        <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-20 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(rgba(155, 142, 87, 0.3) 1px, transparent 1px),
                linear-gradient(90deg, rgba(155, 142, 87, 0.3) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px'
            }} />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="space-y-6">
                <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                  <Icon name="Brain" size={20} style={{ color: '#9B8E57' }} />
                  <span className="text-sm font-semibold" style={{ color: '#9B8E57' }}>
                    Pôle Intelligence Artificielle & Innovation
                  </span>
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                  Intelligence Artificielle
                </h1>
                
                <p className="text-xl md:text-2xl font-semibold" style={{ color: '#9B8E57' }}>
                  Maîtrisez l'IA pour transformer votre organisation et booster votre compétitivité
                </p>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <h3 className="text-lg font-bold mb-3" style={{ color: '#9B8E57' }}>Catalogue de Formation 2026</h3>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    L'Intelligence Artificielle révolutionne tous les secteurs d'activité. Nos formations vous permettent 
                    de comprendre, maîtriser et intégrer l'IA dans votre stratégie d'entreprise avec confiance et efficacité.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    <strong>De l'introduction pour décideurs aux outils d'IA générative, de l'application aux RH et marketing jusqu'à la certification chef de projet IA</strong> : développez une expertise complète pour piloter 
                    votre transformation par l'IA.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Link 
                    to="/contact"
                    className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                    style={{ 
                      backgroundColor: '#96121D',
                      boxShadow: '0 4px 14px rgba(150, 18, 29, 0.4)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#7d0f18';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#96121D';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    Demander un Devis
                    <Icon name="ArrowRight" size={20} className="ml-2" />
                  </Link>
                  
                  <Link 
                    to="/service-request-form"
                    className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-300 border-2"
                    style={{ 
                      borderColor: '#9B8E57',
                      color: '#9B8E57'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#9B8E57';
                      e.target.style.color = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#9B8E57';
                    }}
                  >
                    Télécharger le Catalogue 2026
                    <Icon name="Download" size={20} className="ml-2" />
                  </Link>
                </div>
              </div>

              {/* Right Stats */}
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <div className="text-4xl font-bold mb-2" style={{ color: '#9B8E57' }}>7</div>
                  <p className="text-gray-300">Modules de formation IA spécialisés</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <div className="text-4xl font-bold mb-2" style={{ color: '#9B8E57' }}>100%</div>
                  <p className="text-gray-300">Cas d'usage pratiques et applicables</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <div className="text-4xl font-bold mb-2" style={{ color: '#9B8E57' }}>+50%</div>
                  <p className="text-gray-300">Gain de productivité avec IA générative</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <div className="text-4xl font-bold mb-2" style={{ color: '#9B8E57' }}>Certif.</div>
                  <p className="text-gray-300">Chef de projet IA reconnue</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Modules de Formation Intelligence Artificielle */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center space-x-2 bg-gray-100 rounded-full px-4 py-2 mb-4">
                <Icon name="Cpu" size={20} style={{ color: '#96121D' }} />
                <span className="text-sm font-semibold" style={{ color: '#96121D' }}>
                  Pôle Intelligence Artificielle & Innovation
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Modules de Formation Intelligence Artificielle
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Maîtrisez l'IA de la stratégie à l'opérationnel avec nos 7 modules de formation spécialisés
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {aiModules?.map((module, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-8 border-2 border-gray-200 hover:border-opacity-100 transition-all duration-300 hover:shadow-xl"
                  style={{ borderColor: index % 2 === 0 ? '#96121D' : '#9B8E57' }}
                >
                  <div 
                    className="w-16 h-16 rounded-lg flex items-center justify-center mb-6"
                    style={{ backgroundColor: index % 2 === 0 ? 'rgba(150, 18, 29, 0.1)' : 'rgba(155, 142, 87, 0.1)' }}
                  >
                    <Icon name={module.icon} size={32} style={{ color: index % 2 === 0 ? '#96121D' : '#9B8E57' }} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{module.title}</h3>
                  <p className="text-gray-600 mb-6">{module.description}</p>
                  <ul className="space-y-3">
                    {module.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <Icon 
                          name="CheckCircle" 
                          size={20} 
                          className="mr-3 mt-0.5 flex-shrink-0" 
                          style={{ color: index % 2 === 0 ? '#96121D' : '#9B8E57' }}
                        />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 3: Pourquoi Choisir CRF Perfection */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center space-x-2 bg-white rounded-full px-4 py-2 mb-4 shadow-sm">
                <Icon name="Award" size={20} style={{ color: '#96121D' }} />
                <span className="text-sm font-semibold" style={{ color: '#96121D' }}>
                  Excellence & Expertise
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Pourquoi Choisir CRF Perfection ?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Un partenaire de confiance pour votre transformation par l'Intelligence Artificielle
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {whyChooseUs?.map((reason, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border-t-4"
                  style={{ borderTopColor: index % 2 === 0 ? '#96121D' : '#9B8E57' }}
                >
                  <div 
                    className="w-14 h-14 rounded-full flex items-center justify-center mb-4 mx-auto"
                    style={{ backgroundColor: index % 2 === 0 ? 'rgba(150, 18, 29, 0.1)' : 'rgba(155, 142, 87, 0.1)' }}
                  >
                    <Icon name={reason?.icon} size={28} style={{ color: index % 2 === 0 ? '#96121D' : '#9B8E57' }} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">{reason?.title}</h3>
                  <p className="text-gray-600 text-center">{reason?.description}</p>
                </div>
              ))}
            </div>

            {/* Engagement CRF Perfection */}
            <div className="mt-16 bg-white rounded-2xl p-8 md:p-12 shadow-lg">
              <div className="text-center mb-8">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  Notre Engagement : Plus qu'un cabinet, un partenaire de transformation
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4 mx-auto" style={{ backgroundColor: 'rgba(150, 18, 29, 0.1)' }}>
                    <Icon name="Briefcase" size={24} style={{ color: '#96121D' }} />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">Expertise Terrain</h4>
                  <p className="text-gray-600">Nos formateurs sont des experts IA actifs, confrontés aux réalités de l'intégration IA</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4 mx-auto" style={{ backgroundColor: 'rgba(155, 142, 87, 0.1)' }}>
                    <Icon name="Settings" size={24} style={{ color: '#9B8E57' }} />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">Personnalisation</h4>
                  <p className="text-gray-600">Chaque parcours est conçu sur mesure selon vos enjeux métier spécifiques</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4 mx-auto" style={{ backgroundColor: 'rgba(150, 18, 29, 0.1)' }}>
                    <Icon name="TrendingUp" size={24} style={{ color: '#96121D' }} />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">Vision Futur</h4>
                  <p className="text-gray-600">Nos formations sont alignées sur les technologies IA les plus actuelles et pertinentes</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: CTA Final */}
        <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <Icon name="Rocket" size={20} style={{ color: '#9B8E57' }} />
              <span className="text-sm font-semibold" style={{ color: '#9B8E57' }}>
                Prêt à Transformer Votre Organisation avec l'IA ?
              </span>
            </div>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Bâtissons ensemble votre stratégie IA 2026
            </h2>
            
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Contactez-nous dès aujourd'hui pour discuter de vos besoins en formation Intelligence Artificielle 
              et découvrir comment nous pouvons accélérer votre transformation par l'IA.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                style={{ 
                  backgroundColor: '#96121D',
                  boxShadow: '0 4px 14px rgba(150, 18, 29, 0.4)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#7d0f18';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#96121D';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Demander une Consultation
                <Icon name="ArrowRight" size={20} className="ml-2" />
              </Link>
              
              <Link 
                to="/service-request-form"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-300 border-2"
                style={{ 
                  borderColor: '#9B8E57',
                  color: '#9B8E57'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#9B8E57';
                  e.target.style.color = '#FFFFFF';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#9B8E57';
                }}
              >
                Télécharger le Catalogue
                <Icon name="Download" size={20} className="ml-2" />
              </Link>
            </div>

            <div className="mt-12 pt-8 border-t border-white/20">
              <p className="text-gray-400 mb-4">
                <strong style={{ color: '#9B8E57' }}>CRF Perfection</strong> – Bâtissons ensemble les compétences de demain.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-gray-400">
                <div className="flex items-center">
                  <Icon name="Phone" size={16} className="mr-2" />
                  +1 240-978-4155
                </div>
                <div className="flex items-center">
                  <Icon name="Mail" size={16} className="mr-2" />
                  contact@crfperfection.pro
                </div>
                <div className="flex items-center">
                  <Icon name="MapPin" size={16} className="mr-2" />
                  Washington D.C.
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default AISolutionsIntegrationHubPage;